import { sendRequest } from 'best-modules/vue-utils'
import {
  User,
  UserCredentials,
  UserDelegation,
  UserDelegationDocument,
  UserDelegationCredentials,
} from '@/utils/types/user'
import { Query } from 'best-modules/utils'
import { PaginationList } from 'best-modules/components/server-table/types'

interface UserPermissionsResponse {
  role_permissions: string[]
  direct_permissions: string[]
  denied_permissions?: string[]
}

const userData = (): Promise<User> => {
  return sendRequest({
    name: 'userData',
    method: 'get',
    url: '/json/user/me',
  })
}

const userSingle = (userId: number): Promise<User> => {
  return sendRequest({
    name: 'userSingle',
    method: 'get',
    url: `/json/user/single/${userId}`,
  })
}

const userCreate = (credentials: UserCredentials): Promise<User> => {
  return sendRequest({
    name: 'userCreate',
    method: 'post',
    url: '/json/user/create',
    payload: credentials,
  })
}

const userUpdate = (
  userId: number,
  credentials: UserCredentials
): Promise<User> => {
  return sendRequest({
    name: 'userUpdate',
    method: 'post',
    url: `/json/user/update/${userId}`,
    payload: credentials,
  })
}

const userSendToErm = (userId: number): Promise<void> => {
  return sendRequest({
    name: 'userSendToErm',
    method: 'post',
    url: `/json/user/sendUserErm/${userId}`,
  })
}

const userActivate = (userId: number): Promise<void> => {
  return sendRequest({
    name: 'userActivate',
    method: 'get',
    url: `/json/user/activate/${userId}`,
  })
}

const userDeactivate = (userId: number): Promise<void> => {
  return sendRequest({
    name: 'userDeactivate',
    method: 'get',
    url: `/json/user/deactivate/${userId}`,
  })
}

const userChange = (userId: number): Promise<void> => {
  return sendRequest({
    name: 'userChange',
    method: 'get',
    url: `/json/user/changeUser/${userId}`,
  })
}

const userPermissions = (userId: number): Promise<UserPermissionsResponse> => {
  return sendRequest({
    name: 'userPermissions',
    method: 'get',
    url: `/json/user/${userId}/permissions`,
  })
}

const userPermissionSync = (
  userId: number,
  permissions: string[]
): Promise<UserPermissionsResponse> => {
  return sendRequest({
    name: 'userPermissionSync',
    method: 'post',
    url: `/json/user/${userId}/permissions`,
    payload: { permissions },
  })
}

const userUpdateRole = (
  userId: number,
  roleId: number
): Promise<UserPermissionsResponse> => {
  return sendRequest({
    name: 'userUpdateRole',
    method: 'patch',
    url: `/json/user/${userId}/role`,
    payload: { roleId },
  })
}

const userList = (query: Query): Promise<PaginationList<User>> => {
  return sendRequest({
    name: 'userList',
    method: 'get',
    url: { url: '/json/user/getAll', query },
  })
}

const userDelegationList = (): Promise<UserDelegation[]> => {
  return sendRequest({
    name: 'userDelegationList',
    method: 'get',
    url: '/json/user/delegation',
  })
}

const userDelegationCreate = (
  delegationCredentials: UserDelegationCredentials
): Promise<UserDelegation> => {
  return sendRequest({
    name: 'userDelegationCreate',
    method: 'post',
    url: '/json/user/delegation',
    payload: delegationCredentials,
  })
}

const userDelegationDeactivate = (
  delegationId: number
): Promise<UserDelegation> => {
  return sendRequest({
    name: 'userDelegationDeactivate',
    method: 'patch',
    url: `/json/user/delegation/${delegationId}/deactivate`,
  })
}

const userDelegationRestriction = (
  delegationId: number,
  credentials: {
    docTypeDefinitionId: number
    restrictionTypeIds: number[]
  }
): Promise<any> => {
  return sendRequest({
    name: 'userDelegationRestriction',
    method: 'post',
    url: `/json/user/delegation/${delegationId}/restrictions`,
    payload: {
      restrictions: [credentials],
    },
  })
}

const userDelegationUpdateRestrictions = (
  delegationId: number,
  restrictions: {
    docTypeDefinitionId: number | null
    restrictionTypeIds: number[]
  }[]
): Promise<UserDelegation> => {
  return sendRequest({
    name: 'userDelegationUpdateRestrictions',
    method: 'post',
    url: `/json/user/delegation/${delegationId}/restrictions`,
    payload: { restrictions },
  })
}

const userDelegationSingle = (
  delegationId: number
): Promise<UserDelegation> => {
  return sendRequest({
    name: 'userDelegationSingle',
    method: 'get',
    url: `/json/user/delegation/${delegationId}`,
  })
}

const userDelegationSummary = (
  delegationId: number
): Promise<{ agreements: number; signatures: number }> => {
  return sendRequest({
    name: 'userDelegationSummary',
    method: 'get',
    url: `/json/user/delegation/${delegationId}/summary`,
  })
}

const userDelegationAgreements = (delegationId: number): Promise<any[]> => {
  return sendRequest({
    name: 'userDelegationAgreements',
    method: 'get',
    url: `/json/user/delegation/${delegationId}/agreements`,
  })
}

const userDelegationSignatures = (delegationId: number): Promise<any[]> => {
  return sendRequest({
    name: 'userDelegationSignatures',
    method: 'get',
    url: `/json/user/delegation/${delegationId}/signatures`,
  })
}

const userDelegationDocument = (
  delegationId: number,
  docTypeDocumentId: number
): Promise<UserDelegationDocument> => {
  return sendRequest({
    name: 'userDelegationDocument',
    method: 'get',
    url: `/json/user/delegation/${delegationId}/document/${docTypeDocumentId}`,
  })
}

type NotificationSetting = {
  eventKey: string
  label: string
  description: string
  channelSystem: boolean
  channelEmail: boolean
  channelTelegram: boolean
}

type NotificationSettingUpdate = Pick<
  NotificationSetting,
  'eventKey' | 'channelSystem' | 'channelEmail' | 'channelTelegram'
>

interface UserTelegramStatus {
  telegramChatId: string | null
  telegramConnectedAt: string | null
  hasTelegramPending: boolean
}

type UserTelegramConnectResponse = User & {
  telegramDeepLink?: string
  hasTelegramPending?: boolean
}

const userConnectTelegram = (
  userId: number
): Promise<UserTelegramConnectResponse> => {
  return sendRequest({
    name: 'userConnectTelegram',
    method: 'post',
    url: `/json/user/${userId}/telegram/connect`,
  })
}

const userTelegramStatus = (userId: number): Promise<UserTelegramStatus> => {
  return sendRequest({
    name: 'userTelegramStatus',
    method: 'get',
    url: `/json/user/${userId}/telegram/status`,
  })
}

const userDisconnectTelegram = (userId: number): Promise<User> => {
  return sendRequest({
    name: 'userDisconnectTelegram',
    method: 'delete',
    url: `/json/user/${userId}/telegram/disconnect`,
  })
}

const userAccessLevelCreate = (
  userId: number,
  accessLevelTypeId: number
): Promise<User | void> => {
  return sendRequest({
    name: 'userAccessLevelCreate',
    method: 'post',
    url: `/json/user/${userId}/access-levels`,
    payload: { accessLevelTypeId },
  })
}

const userAccessLevelDelete = (
  userId: number,
  accessLevelTypeId: number
): Promise<User | void> => {
  return sendRequest({
    name: 'userAccessLevelDelete',
    method: 'delete',
    url: `/json/user/${userId}/access-levels/${accessLevelTypeId}`,
  })
}

const userNotificationSettingsGet = (
  userId: number
): Promise<NotificationSetting[]> => {
  return sendRequest({
    name: 'userNotificationSettingsGet',
    method: 'get',
    url: `/json/user/${userId}/notification-settings`,
  })
}

const userNotificationSettingsUpdate = (
  userId: number,
  settings: NotificationSettingUpdate[]
): Promise<NotificationSetting[]> => {
  return sendRequest({
    name: 'userNotificationSettingsUpdate',
    method: 'put',
    url: `/json/user/${userId}/notification-settings`,
    payload: { settings },
  })
}

export {
  userData,
  userCreate,
  userUpdate,
  userSingle,
  userSendToErm,
  userActivate,
  userDeactivate,
  userChange,
  userPermissions,
  userPermissionSync,
  userUpdateRole,
  UserPermissionsResponse,
  userList,
  userDelegationList,
  userDelegationSingle,
  userDelegationCreate,
  userDelegationDeactivate,
  userDelegationRestriction,
  userDelegationUpdateRestrictions,
  userDelegationSummary,
  userDelegationAgreements,
  userDelegationSignatures,
  userDelegationDocument,
  userConnectTelegram,
  userTelegramStatus,
  userDisconnectTelegram,
  UserTelegramStatus,
  UserTelegramConnectResponse,
  userAccessLevelCreate,
  userAccessLevelDelete,
  userNotificationSettingsGet,
  userNotificationSettingsUpdate,
}
